import type { ReactElement, ReactNode } from "react"
import { useState } from "react"
import { navigate } from "gatsby"
import BreakpointVisibilityOrg from "@onestore/hel/dist/components/organisms/BreakpointVisibilityOrg"
import FlexContainerOrg from "@onestore/hel/dist/components/organisms/FlexContainerOrg"
import FlexItemOrg from "@onestore/hel/dist/components/organisms/FlexItemOrg"
import PushOrg from "@onestore/hel/dist/components/organisms/PushOrg"
import _t from "@core/i18n"
import useDomainSearchAiSettingsQuery from "@gatsby-plugin-domain-search/hooks/useDomainSearchAiSettingsQuery"
import {
  chevronRight,
  filledSparkles,
  search,
} from "@ionos-web-design-system/icon/system"
import type { TextProps } from "@ionos-web-design-system/react"
import { Icon, Link, Text } from "@ionos-web-design-system/react"
import UdsThemeProvider from "~/components/UdsThemeProvider"
import { cn } from "~/helpers/cn"
import isEmpty from "~/lib/isEmpty"
import url from "~/lib/url"

type DomainSearchSwitchElementProps = {
  domainSearchInput: ReactNode
  domainSearchPrompt?: ReactNode
  isPromptVersionDefaultActive?: boolean
  hasNotRedirectFooter?: boolean
  footerTextVariant?: TextProps["variant"]
  rightNode?: ReactNode
  isFooterAlignedRight?: boolean
}

type FooterElementProps = {
  textBeforeLink: string
  linkText: string
  onLinkClick: { (): void }
  textVariant?: TextProps["variant"]
}

const FooterElement = ({
  textBeforeLink,
  linkText,
  onLinkClick,
  textVariant = "body",
}: FooterElementProps): ReactElement<FooterElementProps> => {
  return (
    <Text color="base" variant={textVariant}>
      {textBeforeLink}{" "}
      <Link onClick={onLinkClick} title={linkText} concept="mono">
        {linkText}
      </Link>
    </Text>
  )
}

function DomainSearchSwitchElement({
  domainSearchInput,
  domainSearchPrompt,
  isPromptVersionDefaultActive = false,
  hasNotRedirectFooter = false,
  footerTextVariant,
  rightNode,
  isFooterAlignedRight = false,
}: DomainSearchSwitchElementProps): ReactElement<DomainSearchSwitchElementProps> {
  const { url: aiUrl } = useDomainSearchAiSettingsQuery()

  const [isPromptVersionActive, setIsPromptVersionActive] = useState(
    isPromptVersionDefaultActive
  )

  const { enabled } = useDomainSearchAiSettingsQuery()

  // if brand has AI search disabled show classic search input
  if (!enabled) {
    return <>{domainSearchInput}</>
  }

  const handleFooterLinkRedirect = () => {
    const domainSearchUrl = url.getDomainSearchUrl() ?? ""

    if (isEmpty(domainSearchUrl) && isPromptVersionActive) {
      return
    }

    navigate(isPromptVersionActive ? domainSearchUrl : aiUrl, {
      replace: true,
    })
  }

  const footerElementData = {
    textBeforeLink: isPromptVersionActive
      ? _t("domainSearch.ai.inputSwitchText")
      : _t("domainSearch.inputSwitchText"),
    linkText: isPromptVersionActive
      ? _t("domainSearch.ai.inputSwitchLink")
      : _t("domainSearch.inputSwitchLink"),
    onLinkClick: hasNotRedirectFooter
      ? () => setIsPromptVersionActive(!isPromptVersionActive)
      : handleFooterLinkRedirect,
    textVariant: footerTextVariant,
  }

  const footerElementDataMobile = {
    ...footerElementData,
    textBeforeLink: isPromptVersionActive
      ? _t("domainSearch.ai.inputSwitchTextMobile")
      : _t("domainSearch.inputSwitchTextMobile"),
    linkText: isPromptVersionActive
      ? _t("domainSearch.ai.inputSwitchLinkMobile")
      : _t("domainSearch.inputSwitchLink"),
  }

  return (
    <div
      className={cn(isFooterAlignedRight && "lg:flex lg:items-start lg:gap-6")}
    >
      <div className={cn(isFooterAlignedRight && "lg:min-w-0 lg:flex-1")}>
        {isPromptVersionActive ? domainSearchPrompt : domainSearchInput}
      </div>

      <PushOrg
        topSpace={isFooterAlignedRight ? { small: 2, medium: 2, large: 0 } : 2}
      >
        <UdsThemeProvider>
          <div className="text-base">
            <FlexContainerOrg alignItems="center" gapSpace={1}>
              {isPromptVersionActive ? (
                <Icon icon={search} size="medium" />
              ) : (
                <div className="flex size-5 shrink-0 items-center justify-center bg-[linear-gradient(135deg,oklch(0.2908_0.1178_312.75),oklch(0.5887_0.2648_311.67))]">
                  <Icon
                    icon={filledSparkles}
                    size="small"
                    className="text-white"
                  />
                </div>
              )}

              <BreakpointVisibilityOrg from="small" to="large">
                <FlexItemOrg flexGrow={1} flexShrink={1} flexBasis={0}>
                  <FlexContainerOrg alignItems="center" gapSpace={0.5}>
                    <FooterElement {...footerElementDataMobile} />
                    <Icon icon={chevronRight} />
                  </FlexContainerOrg>
                </FlexItemOrg>
              </BreakpointVisibilityOrg>

              <BreakpointVisibilityOrg from="large">
                {isFooterAlignedRight ? (
                  <div>
                    <Text color="base" variant={footerElementData.textVariant}>
                      {footerElementData.textBeforeLink}
                    </Text>

                    <FlexContainerOrg alignItems="center" gapSpace={0.5}>
                      <Link
                        onClick={footerElementData.onLinkClick}
                        title={footerElementData.linkText}
                        concept="mono"
                      >
                        {footerElementData.linkText}
                      </Link>
                      <Icon icon={chevronRight} />
                    </FlexContainerOrg>
                  </div>
                ) : (
                  <FlexItemOrg flexGrow={1} flexShrink={1} flexBasis={0}>
                    <FlexContainerOrg alignItems="center" gapSpace={0.5}>
                      <FooterElement {...footerElementData} />
                      <Icon icon={chevronRight} />
                    </FlexContainerOrg>
                  </FlexItemOrg>
                )}
              </BreakpointVisibilityOrg>

              {rightNode}
            </FlexContainerOrg>
          </div>
        </UdsThemeProvider>
      </PushOrg>
    </div>
  )
}

export default DomainSearchSwitchElement
